import React, { useMemo } from 'react';

import merge from 'lodash/merge';

import { SpotlightTarget } from '@atlaskit/onboarding';
import Popup from '@atlaskit/popup';
import { layers } from '@atlaskit/theme/constants';
import {
	fromRotationsToItemTable,
	mergeRefs,
	PeriodType,
	Rotation,
	SimpleShiftTile,
	Wrap,
} from '@atlassian/eoc-schedule-common';

import { SPOTLIGHT_NAMES } from '../../../../common/constants';
import { useSectionUiPreferences } from '../../../../controllers/preferences';
import { useShift } from '../../../../controllers/shift';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { ActiveCard } from '../active-card';

import { type RotationListProps } from './types';

const RotationList = (props: RotationListProps) => {
	const { rotations, layer, schedule, ...rest } = props;

	const { displayDensity } = useSectionUiPreferences();

	const timeline = rest;

	const itemTable = useMemo(() => fromRotationsToItemTable(rotations), [rotations]);

	const {
		tabbed,
		focusRefs,
		onContainerKeyDown,

		makeOnClick,
		makeOnHover,
		makeOnClose,
		makeOnCross,

		activeSpotlight,
		activeShift,
		selectedShifts,
	} = useShift({ schedule, items: itemTable, now: timeline.nowEpoch });

	return (
		// eslint-disable-next-line @atlaskit/design-system/prefer-primitives, jsx-a11y/no-static-element-interactions
		<div
			// NOTE: might be re add later
			// role="table"
			// aria-label={layer.label}
			// aria-roledescription={intlApi.formatMessage(
			//   scheduleCommonMessages.timelineLayer,
			// )}
			onKeyDown={onContainerKeyDown}
		>
			{rotations.map((rotation) => (
				<Rotation
					key={rotation.id}
					name={rotation.name}
					variant="full"
					size={displayDensity !== 'comfort' ? 'medium' : 'large'}
				>
					{rotation.periods.map((period) => {
						const detail = merge({}, period, { rotation });

						const onClick = makeOnClick(detail);
						const onHover = makeOnHover(detail);
						const onClose = makeOnClose(detail);
						const onCross = makeOnCross(detail);

						return (
							<Popup
								key={period.id}
								content={() => <ActiveCard onClose={onCross} />}
								isOpen={activeShift === period.id}
								onClose={onClose}
								placement="bottom"
								zIndex={layers.navigation() + 1}
								trigger={({ ref: triggerRef }) => (
									<Wrap
										within={(e) => (
											<SpotlightTarget name={SPOTLIGHT_NAMES.shiftTile}>{e}</SpotlightTarget>
										)}
										when={activeSpotlight === period.id}
									>
										<SimpleShiftTile
											ref={mergeRefs(triggerRef, focusRefs[period.id])}
											onMouseEnter={onHover}
											onClick={onClick}
											period={period}
											outerTimeFrame={timeline}
											isSelected={selectedShifts[period.id]}
											isTabbable={tabbed?.id === period.id}
											size={displayDensity !== 'comfort' ? 'medium' : 'large'}
											hasOverrideBorder={
												period.type === PeriodType.Override || period.type === PeriodType.Forwarding
											}
										/>
									</Wrap>
								)}
							/>
						);
					})}
				</Rotation>
			))}
		</div>
	);
};

export { RotationList };
